import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jsonStorage } from './persist';

export interface HistoryEntry {
  roundId: string;
  gameId: string;
  gameName: string;
  bet: number;
  payout: number;
  multiplier: number;
  nonce: number;
  serverSeedHash: string;
  clientSeed: string;
  createdAt: string;
  detail?: string;
}

export interface HistoryState {
  entries: HistoryEntry[];
  add: (entry: HistoryEntry) => void;
  clear: () => void;
  forGame: (gameId: string) => HistoryEntry[];
}

const MAX_ENTRIES = 500;

export const useHistory = create<HistoryState>()(
  persist(
    (set, get) => ({
      entries: [],
      add: (entry) => set({ entries: [entry, ...get().entries].slice(0, MAX_ENTRIES) }),
      clear: () => set({ entries: [] }),
      forGame: (gameId) => get().entries.filter((e) => e.gameId === gameId),
    }),
    {
      name: 'history',
      storage: jsonStorage,
      partialize: (state) => ({ entries: state.entries.slice(0, 200) }),
    },
  ),
);

export interface AggregateStats {
  rounds: number;
  wagered: number;
  returned: number;
  bestMultiplier: number;
  biggestWin: number;
}

export function aggregate(entries: HistoryEntry[]): AggregateStats {
  return entries.reduce<AggregateStats>(
    (acc, e) => ({
      rounds: acc.rounds + 1,
      wagered: acc.wagered + e.bet,
      returned: acc.returned + e.payout,
      bestMultiplier: Math.max(acc.bestMultiplier, e.multiplier),
      biggestWin: Math.max(acc.biggestWin, e.payout),
    }),
    { rounds: 0, wagered: 0, returned: 0, bestMultiplier: 0, biggestWin: 0 },
  );
}
